/* ============================================================================
   AppConfigService — runtime configuration.

   The SPA is built once and configured at deploy time. The container entrypoint
   writes `public/config.json` from environment variables; this service fetches
   it at bootstrap (main.tsx, before the first render) and merges it over the compiled-in
   `environment` defaults. So the SAME image runs in dev/staging/prod with
   different OIDC issuers, API origins, or auth modes — no rebuild.

   Exported as the `appConfigService` singleton.
   ========================================================================== */
import { environment } from '~/environments/environment';
import { computed, signal } from '~/lib/signal';

export type AuthMode = 'mock' | 'bff' | 'oidc';

export interface OidcConfig {
  /** OIDC issuer URL. For Keycloak this is `<base>/realms/<realm>`. */
  issuer: string;
  /** Public client id registered in the realm. */
  clientId: string;
  /** Space-separated scopes. */
  scope: string;
  /** Redirect URI registered at the IdP. Blank → `<origin>/auth/callback`. */
  redirectUri: string;
  /** Post-logout redirect. Blank → `<origin>/login`. */
  postLogoutRedirectUri: string;
}

export interface BffEndpoints {
  login: string;
  logout: string;
  userinfo: string;
  /** SSE bridge for live ticket updates (TicketService.SubscribeTickets→SSE). */
  streamTickets: string;
  /** Attachment upload relay (POST raw file → orchestrates the upload lifecycle). */
  upload: string;
  /** Attachment download relay (GET ?attachmentId → streams the clean blob). */
  download: string;
  /** Agent presence base (POST /heartbeat, POST /status, GET /roster). */
  presence: string;
  /** Google Places address autocomplete base (POST /autocomplete, /details). */
  places: string;
  /** OKF bundle export relay (GET → streams the generated zip). */
  okfExport: string;
  /** OKF bundle import relay (POST raw zip ?target=merge|grounding → ImportSummary). */
  okfImport: string;
}

export interface TicketingConfig {
  /** When true, the app reads/writes the real ticketing service (Connect/gRPC
   *  over JSON). When false, it runs on a seeded mock dataset. */
  enabled: boolean;
  /** Origin prefix for the Connect endpoints. Blank ⇒ same-origin (calls hit
   *  `/ticketing.v1.TicketService/*`, proxied to the service in dev/prod). */
  baseUrl: string;
  /** Tenant the app operates as (every RPC is tenant-scoped). */
  tenantId: string;
}

export interface ControlConfig {
  /** Origin prefix for the control-plane Connect endpoints
   *  (`lumendesk.control.v1.*`). Blank ⇒ same-origin (nginx / the dev proxy
   *  route `/lumendesk.control.v1.*` to the control plane). The setup wizard's
   *  sub-domain step calls `InstanceSetupService` here. */
  baseUrl: string;
}

export interface SetupConfig {
  /** This instance's immutable internal slug — the identity the control-plane
   *  setup RPCs (CheckSubdomainAvailable / SetSubdomain) operate on. Blank ⇒ the
   *  wizard derives it from a `?slug=` param or the `<slug>-console…` hostname. */
  slug: string;
}

export interface AppConfig {
  production: boolean;
  /** Which auth backend the SPA talks to. */
  authMode: AuthMode;
  /** Path-routing base path (e.g. "/acme") when this console is served under a
   *  slug path on a shared host. Blank ⇒ root-hosted. Mirrors the injected
   *  `<base href>`; used to namespace token storage and derive the slug. */
  basePath: string;
  /** Display name of this tenant's workspace (shown in the inbox header /
   *  help-center / settings). Blank ⇒ derived from the slug (title-cased). */
  workspaceName: string;
  /** Same-origin prefix for the data API. */
  apiBase: string;
  /** BFF (token-handler) auth lifecycle endpoints. */
  bff: BffEndpoints;
  /** Direct Keycloak OIDC settings (used when authMode = 'oidc'). */
  oidc: OidcConfig;
  /** Live ticketing-service integration. */
  ticketing: TicketingConfig;
  /** Control-plane integration (the setup wizard's sub-domain step). */
  control: ControlConfig;
  /** Initial-setup-phase parameters. */
  setup: SetupConfig;
}

/** The instance's lifecycle phase, read at bootstrap from the backend's public
 *  config endpoint. 'unknown' when the backend was unreachable. */
export type InstanceMode = 'setup' | 'operational' | 'unknown';

export class AppConfigService {
  private readonly _config = signal<AppConfig>(this.normalize(environment));
  /** Plain mirror of the latest config: Solid batches signal writes, and the
   *  bootstrap reads the config synchronously right after setting it. */
  private current: AppConfig = this._config();
  private readonly _instanceMode = signal<InstanceMode>('unknown');

  /** Reactive, fully-resolved configuration. */
  readonly config = this._config.asReadonly();

  /** The instance lifecycle mode resolved at bootstrap (setup vs operational). */
  readonly instanceMode = this._instanceMode.asReadonly();

  /** Resolved workspace display name (explicit config, else title-cased slug). */
  readonly workspaceName = computed(() => this._config().workspaceName);

  /** Synchronous snapshot for non-reactive callers (services, interceptors). */
  get snapshot(): AppConfig {
    return this.current;
  }

  private setConfig(cfg: AppConfig): void {
    this.current = cfg;
    this._config.set(cfg);
  }

  /** Fetch `config.json` and merge over build-time defaults, then overlay the
   *  operator-configured OIDC from the instance backend. Runs once at startup. */
  async load(): Promise<void> {
    try {
      const url = new URL('config.json', document.baseURI).href;
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) {
        const runtime = (await res.json()) as Partial<AppConfig>;
        const base = environment;
        this.setConfig(
          this.normalize({
            ...base,
            ...runtime,
            bff: { ...base.bff, ...(runtime.bff ?? {}) },
            oidc: { ...base.oidc, ...(runtime.oidc ?? {}) },
            ticketing: { ...base.ticketing, ...(runtime.ticketing ?? {}) },
            control: { ...base.control, ...(runtime.control ?? {}) },
            setup: { ...base.setup, ...(runtime.setup ?? {}) },
          }),
        );
      }
    } catch {
      /* No config.json deployed (or unreachable) — keep compiled-in defaults. */
    }
    await this.loadInstanceConfig();
  }

  /** Overlay the operator-configured admin OIDC from the instance's public config
   *  endpoint, so the console signs staff in against the realm the wizard
   *  configured — not the provision-time platform default baked into config.json
   *  (F28). Also records the lifecycle mode (setup vs operational) for the setup
   *  page (F29) and post-completion status page (F31). Best-effort: an unreachable
   *  backend (e.g. mid-restart after CompleteSetup) leaves the config.json
   *  fallback and mode 'unknown'. Same-origin POST keyed off ticketing.baseUrl, so
   *  it routes under /<slug> in path mode exactly like the other instance RPCs. */
  private async loadInstanceConfig(): Promise<void> {
    try {
      const base = this.snapshot.ticketing.baseUrl;
      const res = await fetch(`${base}/ticketing.v1.PublicConfigService/GetInstanceConfig`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'connect-protocol-version': '1' },
        body: '{}',
        cache: 'no-store',
      });
      if (!res.ok) return;
      const c = (await res.json()) as { mode?: string; oidcIssuer?: string; oidcClientId?: string; oidcScope?: string };
      this._instanceMode.set(
        c.mode === 'SETUP_MODE_OPERATIONAL' ? 'operational' : c.mode === 'SETUP_MODE_SETUP' ? 'setup' : 'unknown',
      );
      const issuer = (c.oidcIssuer ?? '').trim();
      const clientId = (c.oidcClientId ?? '').trim();
      if (issuer || clientId) {
        const cur = this.snapshot;
        this.setConfig(
          this.normalize({
            ...cur,
            oidc: {
              ...cur.oidc,
              issuer: issuer || cur.oidc.issuer,
              clientId: clientId || cur.oidc.clientId,
              scope: (c.oidcScope ?? '').trim() || cur.oidc.scope,
            },
          }),
        );
      }
    } catch {
      /* Backend unreachable — keep config.json's OIDC and mode 'unknown'. */
    }
  }

  /** Fill blank OIDC redirect URIs from the current base (respecting <base href>
   *  so path-routed consoles redirect under their own /<slug>). */
  private normalize(cfg: AppConfig): AppConfig {
    // document.baseURI honours the injected <base href> (e.g.
    // https://app-staging.lumendesk.io/acme/), so blank redirect defaults land
    // under the tenant's path; host-routed consoles fall back to the origin root.
    const base =
      typeof document !== 'undefined' && document.baseURI
        ? document.baseURI.replace(/\/$/, '')
        : typeof location !== 'undefined'
          ? location.origin
          : '';
    // basePath FIRST — the BFF endpoints + workspace-name fallback depend on it.
    // Explicit config wins; else derive from the injected <base href> (path mode)
    // — "/acme/" → "/acme"; root-hosted ("/") → "".
    let basePath = (cfg.basePath ?? '').replace(/\/$/, '');
    if (!basePath && typeof document !== 'undefined' && document.baseURI) {
      const p = new URL(document.baseURI).pathname.replace(/\/$/, '');
      if (p) basePath = p;
    }
    // The BFF endpoints are root-absolute ("/bff/login"). In path mode the gateway
    // routes on the "/<slug>" prefix, so a full-page redirect to a bare "/bff/*"
    // would drop the slug and 404 at the shared host. Prefix them with basePath
    // (the gateway strips it again before nginx forwards to the BFF).
    const bff: BffEndpoints = {
      login: withBasePath(cfg.bff?.login || '/bff/login', basePath),
      logout: withBasePath(cfg.bff?.logout || '/bff/logout', basePath),
      userinfo: withBasePath(cfg.bff?.userinfo || '/bff/userinfo', basePath),
      streamTickets: withBasePath(cfg.bff?.streamTickets || '/bff/stream/tickets', basePath),
      upload: withBasePath(cfg.bff?.upload || '/bff/upload', basePath),
      download: withBasePath(cfg.bff?.download || '/bff/download', basePath),
      presence: withBasePath(cfg.bff?.presence || '/bff/presence', basePath),
      places: withBasePath(cfg.bff?.places || '/bff/places', basePath),
      okfExport: withBasePath(cfg.bff?.okfExport || '/bff/okf/export', basePath),
      okfImport: withBasePath(cfg.bff?.okfImport || '/bff/okf/import', basePath),
    };
    const oidc: OidcConfig = {
      issuer: cfg.oidc?.issuer ?? '',
      clientId: cfg.oidc?.clientId ?? '',
      scope: cfg.oidc?.scope || 'openid profile email',
      redirectUri: cfg.oidc?.redirectUri || `${base}/auth/callback`,
      postLogoutRedirectUri: cfg.oidc?.postLogoutRedirectUri || `${base}/login`,
    };
    const ticketing: TicketingConfig = {
      enabled: cfg.ticketing?.enabled ?? false,
      baseUrl: (cfg.ticketing?.baseUrl ?? '').replace(/\/$/, ''),
      tenantId: cfg.ticketing?.tenantId ?? '',
    };
    const control: ControlConfig = {
      baseUrl: (cfg.control?.baseUrl ?? '').replace(/\/$/, ''),
    };
    const setup: SetupConfig = {
      slug: cfg.setup?.slug ?? '',
    };
    // Workspace name: explicit config wins; else title-case the slug ("acme-co" →
    // "Acme Co") so the header shows the tenant, never a hardcoded demo name.
    const workspaceName = (cfg.workspaceName ?? '').trim() || titleCaseSlug(setup.slug);
    return { ...cfg, basePath, workspaceName, bff, oidc, ticketing, control, setup };
  }
}

/** Prefix a root-absolute path with basePath, unless already prefixed/relative. */
function withBasePath(path: string, basePath: string): string {
  if (!basePath || !path.startsWith('/')) return path;
  if (path === basePath || path.startsWith(basePath + '/')) return path;
  return basePath + path;
}

/** "acme-co" / "acme_co" → "Acme Co"; "" → "". */
function titleCaseSlug(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export const appConfigService = new AppConfigService();
